import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../Utils/Axios';

const resolveResumeUrl = (resumePath) => {
  if (!resumePath) return '';
  if (/^https?:\/\//i.test(resumePath)) return resumePath;
  const base = process.env.REACT_APP_SERVER_ADDRESS || api?.defaults?.baseURL || 'http://localhost:8000';
  return `${String(base).replace(/\/+$/, '')}${resumePath.startsWith('/') ? resumePath : `/${resumePath}`}`;
};

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString();
};

const ApplicationDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/api/hospital/applications/${id}`);
        setApplication(res.data?.application || null);
      } catch (err) {
        setApplication(null);
        setError(err?.response?.data?.message || 'Failed to load application details');
      } finally {
        setLoading(false);
      }
    };

    if (id) run();
  }, [id]);

  const resumeUrl = useMemo(() => resolveResumeUrl(application?.resume || ''), [application?.resume]);

  if (loading) {
    return (
      <div className="col-lg-12">
        <div className="table-wrapper2">
          <p>Loading application details...</p>
        </div>
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="col-lg-12">
        <div className="table-wrapper2">
          <p>{error || 'Application not found'}</p>
          <button className="primry-btn-2 lg-btn mt-3" onClick={() => navigate('/hospital-applications')}>
            Back to Applications
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="col-lg-12">
      <div className="dashboard-inner">
        <div className="author-and-action-btn-area two mb-35">
          <div className="author-area two">
            <div className="author-content">
              <span>Hospital Dashboard</span>
              <h4>Application Overview</h4>
              <p>{application.jobTitle || 'Job Application'}</p>
            </div>
          </div>
          <div className="action-btn-group">
            <Link className="primry-btn-2 lg-btn" to="/hospital-applications">
              Back
            </Link>
            {resumeUrl && (
              <a className="primry-btn-1 lg-btn" href={resumeUrl} target="_blank" rel="noreferrer">
                Open CV
              </a>
            )}
          </div>
        </div>

        <div className="counter-area mb-30">
          <div className="row g-lg-4 g-md-4 gy-4">
            <div className="col-lg-4 col-md-6">
              <div className="counter-single">
                <div className="coundown">
                  <p>Applicant</p>
                  <h3 style={{ fontSize: 22 }}>{application.applicantName || '-'}</h3>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="counter-single two">
                <div className="coundown">
                  <p>Status</p>
                  <h3 style={{ fontSize: 22 }}>{application.status || 'Submitted'}</h3>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="counter-single three">
                <div className="coundown">
                  <p>Applied On</p>
                  <h3 style={{ fontSize: 22 }}>{new Date(application.createdAt).toLocaleDateString()}</h3>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="job-post-area">
          <div className="form-wrapper" style={{ borderRadius: 16 }}>
            <div className="row">
              <div className="col-lg-12">
                <div className="section-title2 mb-20">
                  <h5>Submitted Application Form</h5>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-inner mb-25">
                  <label>Job Title</label>
                  <input type="text" readOnly value={application.jobTitle || '-'} />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-inner mb-25">
                  <label>Applied At</label>
                  <input type="text" readOnly value={formatDate(application.createdAt)} />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-inner mb-25">
                  <label>Applicant Name</label>
                  <input type="text" readOnly value={application.applicantName || '-'} />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-inner mb-25">
                  <label>Email</label>
                  <input type="text" readOnly value={application.applicantEmail || '-'} />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-inner mb-25">
                  <label>Phone</label>
                  <input type="text" readOnly value={application.phone || '-'} />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-inner mb-25">
                  <label>Gender</label>
                  <input type="text" readOnly value={application.gender || '-'} />
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-inner mb-25">
                  <label>Qualification</label>
                  <input type="text" readOnly value={application.qualification || '-'} />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-inner mb-25">
                  <label>Notice Period</label>
                  <input type="text" readOnly value={application.noticePeriod || '-'} />
                </div>
              </div>

              <div className="col-md-12">
                <div className="form-inner mb-25">
                  <label>Cover Letter</label>
                  <textarea rows={7} readOnly value={application.coverLetter || 'No cover letter provided by applicant.'} />
                </div>
              </div>

              <div className="col-md-12">
                <div
                  style={{
                    border: '1px solid #e4ebf3',
                    borderRadius: 12,
                    padding: 16,
                    background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
                  }}
                >
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div>
                      <h6 style={{ marginBottom: 4 }}>Applicant CV</h6>
                      <p style={{ margin: 0, color: '#6b7280' }}>
                        View or download the resume submitted with this application.
                      </p>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                      {resumeUrl ? (
                        <>
                          <a className="primry-btn-2 lg-btn" href={resumeUrl} target="_blank" rel="noreferrer">
                            <i className="bi bi-eye-fill" style={{ marginRight: 8 }} />
                            View CV
                          </a>
                          <a className="primry-btn-1 lg-btn" href={resumeUrl} download>
                            <i className="bi bi-download" style={{ marginRight: 8 }} />
                            Download CV
                          </a>
                        </>
                      ) : (
                        <span>No CV found.</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationDetails;
